import React from "react";
import moment from "moment";
import { styled } from "@material-ui/styles";
import { injectIntl } from "react-intl";
import { ITeam } from "../../types";
import { IStyledArguments } from "../../types/styled-arguments";
import { dayMapper } from "./nextTraining";
import messages from "./PageDashboard.intl";

const Wrapper = styled("div")(({ theme }: IStyledArguments) => ({
  background: "#ffffff",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 16,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  padding: theme.spacing(2, 2.25),
}));

const Header = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 14,
});

const Title = styled("span")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "0.9rem",
  color: theme.palette.text.primary,
}));

const Legend = styled("span")(({ theme }: IStyledArguments) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  fontSize: "0.68rem",
  color: theme.palette.text.secondary,
  "&::before": {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: theme.palette.extra.warning,
  },
}));

const DaysRow = styled("div")({
  display: "flex",
  justifyContent: "space-between",
});

const Day = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
});

const WeekdayLabel = styled("span")<{ $today?: boolean }>(
  ({ theme, $today }: IStyledArguments & { $today?: boolean }) => ({
    fontSize: "0.62rem",
    fontWeight: $today ? 700 : 500,
    color: $today ? theme.palette.primary.dark : theme.palette.text.secondary,
  })
);

const DateBadge = styled("span")<{ $training?: boolean; $today?: boolean; $past?: boolean }>(
  ({ theme, $training, $today, $past }: IStyledArguments & { $training?: boolean; $today?: boolean; $past?: boolean }) => ({
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: 700,
    fontSize: "0.78rem",
    background: $training ? theme.palette.extra.warning : "transparent",
    color: $training ? "#ffffff" : $past ? "rgba(16, 21, 26, 0.3)" : theme.palette.text.primary,
    border: $today && !$training ? `1.5px solid ${theme.palette.primary.main}` : "none",
  })
);

interface IWeekStrip {
  teams: ITeam[];
}

const WeekStrip = injectIntl<IWeekStrip>(({ teams, intl }) => {
  const today = moment();
  const monday = moment(today).isoWeekday(1);
  const week = Array.from({ length: 7 }, (_, i) => moment(monday).add(i, "days"));

  const trainingWeekdays = new Set<number>();
  teams.forEach((team) => {
    Object.entries(dayMapper).forEach(([isoWeekday, key]) => {
      if (team.trainingSetup && team.trainingSetup[key]) {
        trainingWeekdays.add(Number(isoWeekday));
      }
    });
  });

  return (
    <Wrapper>
      <Header>
        <Title>{intl.formatMessage(messages.thisWeek)}</Title>
        <Legend>{intl.formatMessage(messages.legendTrainings)}</Legend>
      </Header>
      <DaysRow>
        {week.map((date) => {
          const isToday = date.isSame(today, "day");
          const isPast = date.isBefore(today, "day");
          const isTraining = trainingWeekdays.has(date.isoWeekday());
          return (
            <Day key={date.format("YYYY-MM-DD")}>
              <WeekdayLabel $today={isToday}>{date.format("dd").slice(0, 1).toUpperCase()}</WeekdayLabel>
              <DateBadge $training={isTraining} $today={isToday} $past={isPast}>
                {date.date()}
              </DateBadge>
            </Day>
          );
        })}
      </DaysRow>
    </Wrapper>
  );
});

export default WeekStrip;
